import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { isProductionDomain } from '../../utils/domain';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import Spinner from '../../components/Spinner';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import './ClaimListing.css';

interface ListingType {
    id: string;
    title: string;
    description: string;
    monthly_rent: number;
    address: string;
    latitude?: number | null;
    longitude?: number | null;
    pictures?: string[];
    unit_type?: string;
    lease_term?: string;
    facebook_first_name?: string;
    facebook_last_name?: string;
    facebook_avatar_url?: string;
    source?: string;
    user_id?: string;
    university_id?: number;
}

// Facebook Listings user ID (matches backend constant)
const FACEBOOK_LISTINGS_USER_ID = "user_1765811664056";

const ClaimListing: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const { isDarkMode } = useTheme();
    const [listing, setListing] = useState<ListingType | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [isAlreadyClaimed, setIsAlreadyClaimed] = useState(false);

    useEffect(() => {
        // Only allow on production domain
        if (!isProductionDomain()) {
            navigate('/');
            return;
        }

        // If already authenticated, redirect to listing
        if (isAuthenticated && id) {
            navigate(`/listing/${id}`);
            return;
        }

        fetchListing();
    }, [id, isAuthenticated, navigate]);

    const fetchListing = async () => {
        if (!id) {
            setError('Invalid listing ID');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/subleases/${id}`);
            if (!response.ok) {
                throw new Error('Listing not found');
            }
            const data = await response.json();
            setListing(data);

            // Check if listing is already claimed (has a user_id that's not the Facebook Listings user)
            const userId = data.user_id;
            if (userId && userId !== FACEBOOK_LISTINGS_USER_ID) {
                setIsAlreadyClaimed(true);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load listing');
        } finally {
            setLoading(false);
        }
    };

    const formatPrice = (price: number) => {
        if (!price) return 'N/A';
        return `$${price.toString()}`;
    };

    const getImageUrl = (index: number) => {
        if (!listing?.pictures || !listing.pictures[index]) return undefined;
        const pic = listing.pictures[index];
        return pic.startsWith('http') ? pic : `${API_BASE_URL}${pic}`;
    };

    if (loading) {
        return (
            <>
                <NavBar inverted={true} />
                <div className={`claim-listing-page ${isDarkMode ? 'dark-mode' : ''}`}>
                    <div className="claim-container">
                        <Spinner size="large" />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !listing) {
        return (
            <>
                <NavBar inverted={true} />
                <div className={`claim-listing-page ${isDarkMode ? 'dark-mode' : ''}`}>
                    <div className="claim-container">
                        <div className="error-message">
                            <h2>Listing Not Found</h2>
                            <p>{error || 'The listing you are looking for does not exist.'}</p>
                            <Link to="/" className="btn-primary">
                                Go to Home
                            </Link>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // If listing is already claimed, show a message
    if (isAlreadyClaimed) {
        return (
            <>
                <NavBar inverted={true} />
                <div className={`claim-listing-page ${isDarkMode ? 'dark-mode' : ''}`}>
                    <div className="claim-container">
                        <div className="claim-content">
                            <div className="claim-header">
                                <h1>Listing Already Claimed</h1>
                                <p className="claim-subtitle">
                                    This listing has already been claimed by its owner.
                                </p>
                            </div>

                            <div className="listing-preview-simple">
                                <h2>{listing.title}</h2>
                                <div className="listing-price">{formatPrice(listing.monthly_rent)}/mo</div>
                                {listing.pictures && listing.pictures.length > 0 && (
                                    <div className="listing-preview-image">
                                        <img
                                            src={getImageUrl(0)}
                                            alt={listing.title}
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="claim-cta-simple">
                                <Link to={`/listing/${id}`} className="btn-primary btn-large">
                                    View Listing
                                </Link>
                                <p className="already-have-account">
                                    <Link to="/">Browse other listings</Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <NavBar inverted={true} />
            <div className={`claim-listing-page ${isDarkMode ? 'dark-mode' : ''}`}>
                <div className="claim-container">
                    <div className="claim-content">
                        <div className="claim-header">
                            <h1>Claim Your Listing</h1>
                            <p className="claim-subtitle">
                                Sign up to claim this listing and receive messages from other users.
                            </p>
                        </div>

                        <div className="listing-preview-simple">
                            <h2>{listing.title}</h2>
                            <div className="listing-price">{formatPrice(listing.monthly_rent)}/mo</div>
                            {listing.pictures && listing.pictures.length > 0 && (
                                <div className="listing-preview-image">
                                    <img
                                        src={getImageUrl(0)}
                                        alt={listing.title}
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="claim-cta-simple">
                            <Link
                                to={`/signup?redirect=/listing/${id}&listingId=${id}&firstName=${encodeURIComponent(listing.facebook_first_name || '')}&lastName=${encodeURIComponent(listing.facebook_last_name || '')}&universityId=${listing.university_id || ''}`}
                                className="btn-primary btn-large"
                            >
                                Sign Up to Claim Listing
                            </Link>
                            <p className="already-have-account">
                                Already have an account? <Link to={`/profile?redirect=/listing/${id}`}>Sign in</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default ClaimListing;
